 
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AdminUserDto } from "../lib/auth-api.js";
import {
  clearAdminJwt,
  getAdminJwt,
  setAdminJwt,
} from "../lib/auth-storage.js";
import { clearAdminApiKey } from "../lib/admin-settings.js";
import {
  AuthApiError,
  fetchAdminMe,
  loginAdmin,
  logoutAdmin,
  verifyAdminMfaLogin,
} from "../lib/auth-api.js";
import { setTenantExternalId } from "../lib/tenant-settings.js";

interface AuthState {
  user: AdminUserDto | null;
  loading: boolean;
  error: string | null;
}

export type LoginStepResult =
  | AdminUserDto
  | { mfaRequired: true; mfaToken: string };

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<LoginStepResult>;
  completeMfaLogin: (mfaToken: string, code: string) => Promise<AdminUserDto>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUserDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshMe = useCallback(async () => {
    const token = getAdminJwt();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await fetchAdminMe();
      setUser(me);
      if (
        (me.role === "tenant_admin" || me.role === "tenant_operator") &&
        me.tenantExternalId
      ) {
        setTenantExternalId(me.tenantExternalId);
      }
      setError(null);
    } catch (e) {
      // Only a genuine 401 (expired/invalid token) destroys the session. For a
      // 429 (rate limit), 5xx, or network error, keep the existing token+user so
      // the next navigation recovers. Critically, do NOT retry here: retrying a
      // rate-limited /admin/auth/me immediately just amplifies the 429 storm
      // (the burst of identical /me requests the operator was seeing).
      if (e instanceof AuthApiError && e.status === 401) {
        clearAdminJwt();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email: string, password: string): Promise<LoginStepResult> => {
    setError(null);
    setLoading(true);
    try {
      const r = await loginAdmin(email, password);
      if ("mfaRequired" in r && r.mfaRequired === true) {
        return { mfaRequired: true, mfaToken: r.mfaToken };
      }
      if ("mfaEnrollmentRequired" in r && r.mfaEnrollmentRequired) {
        setAdminJwt(r.token);
        setUser(r.user);
        if (
          (r.user.role === "tenant_admin" || r.user.role === "tenant_operator") &&
          r.user.tenantExternalId
        ) {
          setTenantExternalId(r.user.tenantExternalId);
        }
        return r.user;
      }
      setAdminJwt(r.token);
      setUser(r.user);
      if (
        (r.user.role === "tenant_admin" || r.user.role === "tenant_operator") &&
        r.user.tenantExternalId
      ) {
        setTenantExternalId(r.user.tenantExternalId);
      }
      return r.user;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Login failed";
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const completeMfaLogin = useCallback(async (mfaToken: string, code: string) => {
    setError(null);
    setLoading(true);
    try {
      const { token, user: u } = await verifyAdminMfaLogin(mfaToken, code.trim());
      setAdminJwt(token);
      setUser(u);
      if (
        (u.role === "tenant_admin" || u.role === "tenant_operator") &&
        u.tenantExternalId
      ) {
        setTenantExternalId(u.tenantExternalId);
      }
      return u;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "MFA verification failed";
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutAdmin();
    } finally {
      clearAdminJwt();
      clearAdminApiKey();
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      login,
      completeMfaLogin,
      logout,
      refreshMe,
    }),
    [user, loading, error, login, completeMfaLogin, logout, refreshMe]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

/** For providers (e.g. ThemeProvider) that may render outside AuthProvider in tests. */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
